import {
  Col,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Typography,
  Upload,
  UploadFile,
} from 'antd'
import { RcFile, UploadChangeParam } from 'antd/es/upload'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  useCreateRequestMoney,
  useGetConfigurationFee,
  useGetExchangeRate,
} from '../../../api/send-money'
import { ROOT_URL } from '../../../App'
import CloudArrowUp from '../../../common/images/png/CloudArrowUp.png'
import CloudArrowUpDisabled from '../../../common/images/png/CloudArrowUpDisabled.png'
import { ReactComponent as CaretDown } from '../../../common/images/svg/CaretDown.svg'
import { ReactComponent as CheckIcon } from '../../../common/images/svg/Check.svg'
import { ReactComponent as CheckSelectIcon } from '../../../common/images/svg/CheckSelect.svg'
import smartLogoDefault from '../../../common/images/svg/smart-logo-icon.svg'
import { ReactComponent as ArrowIcon } from '../../../common/images/svg/SuffixSelectIcon.svg'
import { ReactComponent as TrashIcon } from '../../../common/images/svg/Trash.svg'
import { CustomButton } from '../../../components/button/buttonCustom'
import { optionPurpostOfRemitter } from '../../../components/inputs/select/purposeOfRemittance.constants'
import LoadingOverlay from '../../../components/loading/loadingOverlay'
import ConfirmModal from '../../../components/modal/confirmModal'
import { useUserDetails } from '../../../components/pages/auth/components/auth/hooks'
import { TOKEN_KEY } from '../../../components/providers/authProvider'
import { useNotification } from '../../../components/providers/notification/useNotification'
import CompanyDefault from '../../../pages/send-money/component/company-default'
import SelectBankAccountProvider from '../../../pages/send-money/component/select-bank-account-provider'
import SelectWalletProvider from '../../../pages/send-money/component/select-wallet-provider'
import TextFee from '../../../pages/send-money/component/text-fee'
import PaymentStatus from '../../../types/paymentStatus.type'
import { getUniqueFileName } from '../../../utils/beforeUpload'
import {
  acceptUploadFileTypes,
  allowedUploadFileTypesMessage,
  SUPPORT_DOC_ALLOWED_EXT,
  SUPPORT_DOC_ALLOWED_MIMES,
} from '../../../utils/acceptUploadFileTypes'
import { clearFieldErrorMessageOnChange } from '../../../utils/form.constants'
import useResponsive from '../../../utils/hooks/useResponsive'
import {
  formatNumber,
  formatNumberOnChangeInput,
  formatNumberV2,
  parseFormattedNumberInput,
  roundTo,
  roundUp,
} from '../../../utils/list.utils'
import { FormTransferProps, listPayFee } from './form-transfer-props'
import SelectSender from './select-sender'

const { TextArea } = Input
const { Option, OptGroup } = Select

const MAX_SIZE = 10 * 1024 * 1024
const MAX_FILE_COUNT = 5

export const Direction = { PAY_TO_BEN: 'PAY_TO_BEN', BEN_TO_PAY: 'BEN_TO_PAY' }

// Helper function to find currency by symbol
const findCurrencyBySymbol = (dataAllCurrency: any[], symbol: string) => {
  return dataAllCurrency.find((item: any) => item.symbol === symbol) as any
}

// Helper function to get default value for currency
const getDefaultCurrencyValue = (
  currencyId: number | undefined,
  currencies: any,
  hasExistingValue: boolean,
  existingValue: string,
  firstCalculate?: boolean
) => {
  if (!currencyId) return '0'

  const currencyETH = findCurrencyBySymbol(currencies, 'ETH')
  const currencyUSD = findCurrencyBySymbol(currencies, 'USD')
  const currencyUSDT = findCurrencyBySymbol(currencies, 'USDT')
  const currencyDAI = findCurrencyBySymbol(currencies, 'DAI')
  const currencyEUR = findCurrencyBySymbol(currencies, 'EUR')
  const currencyHKD = findCurrencyBySymbol(currencies, 'HKD')
  const currencyAUD = findCurrencyBySymbol(currencies, 'AUD')
  const currencyJPY = findCurrencyBySymbol(currencies, 'JPY')
  const currencyCHF = findCurrencyBySymbol(currencies, 'CHF')
  const currencyCNY = findCurrencyBySymbol(currencies, 'CNY')
  const currencyAED = findCurrencyBySymbol(currencies, 'AED')
  const currencySGD = findCurrencyBySymbol(currencies, 'SGD')
  const currencyGBP = findCurrencyBySymbol(currencies, 'GBP')

  if (hasExistingValue) return existingValue

  if (firstCalculate) {
    return '0.00'
  }

  switch (currencyId) {
    case currencyUSD?.id:
      return '1000.00'
    case currencyEUR?.id:
      return '1000.00'
    case currencyETH?.id:
      return '1.000000'
    case currencyHKD?.id:
    case currencyAUD?.id:
    case currencyJPY?.id:
    case currencyCHF?.id:
    case currencyCNY?.id:
    case currencyAED?.id:
    case currencySGD?.id:
    case currencyGBP?.id:
      return '1000.00'
    case currencyUSDT?.id:
    case currencyDAI?.id:
      return '1000.0000'
    default:
      return '0'
  }
}

const requiredFields = [
  'sender',
  'beneficiary_value',
  'payment_value',
  'companies_default',
]

const FormTransfer = ({
  beneficiaryCurrency,
  setBeneficiaryCurrency,
  paymentCurrency,
  setPaymentCurrency,
  form,
  companyDefaultSelected,
  setCompanyDefaultSelected,
  companyDefaultCurrency,
  dataCompaniesDefault,
  loadingDefaulCompany,
  loadingCompanyCurrency,
  setSearchDefaultCompany,
  paramsWalletProvider,
  setParamsWalletProvider,
  walletSelectedProvider,
  setWalletSelectedProvider,
  dataListWalletProvider,
  paramsBankAccountProvider,
  setParamsBankAccountProvider,
  bankAccountSelectedProvider,
  setBankAccountSelectedProvider,
  dataListBankAccountProvider,
  dataListSender,
  paramsSender,
  setParamsSender,
  senderSelected,
  setSenderSelected,
  getDetailRequestMoney,
  loadingDetail,
  setAddSender,
  dataAllCurrency,
}: FormTransferProps) => {
  const { isTablet } = useResponsive()
  const { open: openNotification } = useNotification()
  const detailUser = useUserDetails()
  const isSettingDefaultRef = useRef(false)
  const hasSetDefaultRef = useRef(false)
  const isValidatingAfterCurrencyChangeRef = useRef(false)
  const previousExchangeRateRef = useRef<number | null>(null)
  const isExceedingMaxLimitRef = useRef(false)
  const isChangingCurrencyRef = useRef(false)
  const hasUserEditedInputRef = useRef(false)
  const isTypingPaymentValueRef = useRef(false)
  const isTypingBeneficiaryValueRef = useRef(false)
  const [checked, setChecked] = useState(listPayFee[1].value)
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false)
  const [beneficiaryAmount, setBeneficiaryAmount] = useState(0)
  const [paymentAmount, setPaymentAmount] = useState(0)
  const [exchangeRate, setExchangeRate] = useState(1)
  const [paymentDecimal, setPaymentDecimal] = useState(2)
  const [beneficiaryDecimal, setBeneficiaryDecimal] = useState(6)
  const [direction, setDirection] = useState(Direction.PAY_TO_BEN)
  const [lastChange, setLastChange] = useState('BENEFICARY')
  const [beneficiaryCurrencySymbol, setBeneficiaryCurrencySymbol] =
    useState('USD')
  const [paymentCurrencySymbol, setPaymentCurrencySymbol] = useState('EUR')
  const [showReviewFee, setShowReviewFee] = useState(false)
  const [configFee, setConfigFee] = useState<any>(null)
  const [transferFee, setTransferFee] = useState<any>(0)
  const [serviceFee, setServiceFee] = useState<any>(0)
  const [feeConfigId, setFeeConfigId] = useState<any>(undefined)
  const [beneficiaryValueError, setBeneficiaryValueError] = useState(false)
  const [openCompanyDefault, setOpenCompanyDefault] = useState(false)
  const [disabledProvider, setDisabledProvider] = useState(false)
  const [firstCalculate, setFirstCalculate] = useState(true)
  const [listBankAccountProvider, setListBankAccountProvider] = useState([])
  const [listWalletProvider, setListWalletProvider] = useState([])
  const [listSender, setListSender] = useState([])
  const [created, setCreated] = useState(false)
  const [loadingDefaultValue, setLoadingDefaultValue] = useState(true)
  const [purpose, setPurpose] = useState(false)

  const [document, setDocument] = useState<UploadChangeParam<UploadFile<any>>>({
    file: {} as UploadFile<any>,
    fileList: [],
  })

  const specialIntructionsDraft = `Ref ${detailUser?.smartUID || ''}`
  const payment_value = Form.useWatch('payment_value', form)
  const beneficiary_value = Form.useWatch('beneficiary_value', form)
  const sender = Form.useWatch('sender', form)
  const companies_default = Form.useWatch('companies_default', form)
  const providerBankAccount = Form.useWatch('providerBankAccount', form)
  const providerWallet = Form.useWatch('providerWallet', form)

  const userId = useMemo(() => {
    try {
      return detailUser ? detailUser?.id || 0 : 0
    } catch {
      return 0
    }
  }, [detailUser])

  const {
    data: rate,
    isLoading: loadingRate,
    refetch: refetchRate,
  } = useGetExchangeRate({ from: paymentCurrency, to: beneficiaryCurrency })

  const { mutate: createRequestMoney, isLoading: loadingCreate } =
    useCreateRequestMoney({
      onSuccess: (response: { data: { message: string; data: any } }) => {
        getDetailRequestMoney(response?.data?.data?.id)
        setCreated(true)
        if (openNotification) {
          openNotification({
            key: 'create_success',
            message: 'Order submitted successfully!',
            type: 'success',
          })
        }
      },
      onError: (error: { message: string; reason_code: string }) => {
        if (error?.reason_code === 'ERR00104') {
          setShowReviewFee(true)
        } else if (openNotification) {
          openNotification({
            key: 'create_failed',
            message: error.message || 'Create order failed!',
            type: 'error',
          })
        }
      },
    })

  const { mutate: fetConfigurationFee, isLoading: loadingFee } =
    useGetConfigurationFee()

  const fetchFee = (currencyId: number | undefined) => {
    const currency = dataAllCurrency?.find(
      (item: any) => item?.id === currencyId
    )
    fetConfigurationFee(currencyId, {
      onSuccess: (res) => {
        if (currencyId === beneficiaryCurrency) {
          setConfigFee(res.data.data)
        }
      },
      onError: () => {
        // Error handling is done by the mutation hook
      },
    })
  }

  const onSubmitOrder = (values: any) => {
    if (!bankAccountSelectedProvider && !walletSelectedProvider) {
      if (openNotification) {
        openNotification({
          key: 'fee_error',
          message: 'Please select provider.',
          type: 'error',
        })
      }
      throw new Error('Validation failed')
    }

    const fileList = values?.supportDocument?.fileList ?? []
    const documents = fileList
      .filter((f: UploadFile) => f.status === 'done' && f.response)
      .map((f: UploadFile) => f.response as string)
    const specialInstructions = values?.special_intructions;
    const specialInstructionsDetail = specialInstructions && specialInstructions.startsWith(specialIntructionsDraft)
      ? specialInstructions.slice(specialIntructionsDraft.length).trim() ?? undefined
      : specialInstructions || undefined;

    // Normalize numeric amounts to correct decimals before submit
    const normalizedPaymentAmount = Number(
      paymentAmount.toFixed(paymentDecimal ?? 2)
    )

    const normalizedBeneficiaryAmount = Number(
      beneficiaryAmount.toFixed(beneficiaryDecimal ?? 2)
    )

    const body = {
      customerId: userId,
      paymentAmount: normalizedPaymentAmount,
      receiverAmount: normalizedBeneficiaryAmount,
      companyId: senderSelected?.id,
      receiveCurrencyId: beneficiaryCurrency,
      paymentCurrencyId: paymentCurrency,
      specialInstructions: specialIntructionsDraft ?? undefined,
      specialInstructionsDetail,
      documents,
      providerWalletId: walletSelectedProvider?.id,
      feesPayer: checked === listPayFee[0].value ? 'SENDER' : 'BENEFICIARY',
      feeConfigVersion: configFee?.version,
      dateOfTransaction: values?.send_date ?? dayjs(),
      serviceProviderId: companyDefaultSelected?.id,
      providerBankAccountId: bankAccountSelectedProvider?.id || undefined,
      feeConfigId,
      exchangeRate,
      usdToFeeCurrencyRate: configFee?.usdToFeeCurrencyRate,
      status: PaymentStatus.PENDING,
      direction,
      purposeOfRemittance: values?.purposeOfRemittance,
    }
    createRequestMoney(body)
  }

  const onOpenChange = useCallback((open: boolean) => {
    setOpenCompanyDefault(open)
  }, [])

  const handleRemoveFile = (fileToRemove: UploadFile) => {
    const newFileList = document.fileList.filter(
      (file) => file.uid !== fileToRemove.uid
    )
    if (newFileList.length === 0) {
      form.setFieldValue('supportDocument', undefined)
      form.validateFields(['supportDocument'])
    }
    setDocument({ file: {} as UploadFile<any>, fileList: newFileList })
    form.setFieldValue('supportDocument', {
      file: {} as UploadFile<any>,
      fileList: newFileList,
    })
  }

  let uploadErrorShown = false

  const handleChangeFile = (info: UploadChangeParam<UploadFile<any>>) => {
    const { file, fileList } = info

    const existingFiles: UploadFile[] =
      form.getFieldValue('supportDocument')?.fileList ?? []
    const totalFiles = existingFiles.length
    if (totalFiles > MAX_FILE_COUNT) {
      if (!uploadErrorShown) {
        openNotification?.({
          key: 'file_count',
          message: `You can upload up to ${MAX_FILE_COUNT} files only!`,
          type: 'error',
        })
      }
      uploadErrorShown = true
      setTimeout(() => {
        uploadErrorShown = false
      }, 500)
      form.setFieldValue('supportDocument', document)
      return
    }
    setDocument({ file, fileList })
    form.setFieldValue('supportDocument', { file, fileList })
  }

  const beforeUpload = (file: RcFile) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    const isValidType =
      SUPPORT_DOC_ALLOWED_MIMES.has(file.type) ||
      (SUPPORT_DOC_ALLOWED_EXT.has(ext) &&
        (!file.type || file.type === 'application/octet-stream'))

    if (!isValidType) {
      openNotification?.({
        key: 'file_type',
        message: allowedUploadFileTypesMessage,
        type: 'error',
      })
      return Upload.LIST_IGNORE
    }

    if (file.size > MAX_SIZE) {
      openNotification?.({
        key: 'file_size',
        message: `${file.name} is too big, max size is 10 MB!`,
        type: 'error',
      })
      return Upload.LIST_IGNORE
    }

    const currentFiles: UploadFile[] =
      form.getFieldValue('supportDocument')?.fileList ?? []

    return getUniqueFileName(file, currentFiles)
  }

  const handleContinueReview = () => {
    fetchFee(beneficiaryCurrency)
    setShowReviewFee(false)
  }

  useEffect(() => {
    if (!beneficiaryCurrency || created) return
    fetchFee(beneficiaryCurrency)
    const intervalId = setInterval(() => {
      fetchFee(beneficiaryCurrency)
    }, 60000)
    return () => clearInterval(intervalId)
  }, [beneficiaryCurrency, created])

  // Re-validate beneficiary_value after fee is fetched to ensure validation uses correct fee data
  useEffect(() => {
    if (!configFee || loadingFee || isChangingCurrencyRef.current) return

    const beneficiaryValue = form.getFieldValue('beneficiary_value')
    if (beneficiaryValue && Number(beneficiaryValue) > 0) {
      setTimeout(() => {
        form.validateFields(['beneficiary_value']).catch(() => {
          // Error is expected if value is invalid, just ensure validation runs
        })
      }, 100)
    }
  }, [configFee, loadingFee, beneficiaryCurrency, form])

  useEffect(() => {
    if (!rate) return
    setExchangeRate(rate?.data?.data || 1)
  }, [rate])

  useEffect(() => {
    if (!rate) return
    if (!firstCalculate) return
    const paymentValue = (0 / rate?.data?.data).toFixed(paymentDecimal)
    form.setFieldsValue({
      payment_value: formatNumberV2(paymentValue, paymentDecimal),
    })
    setPaymentAmount(Number(paymentValue))
    setFirstCalculate(false)
  }, [rate, paymentDecimal, form, firstCalculate])

  // Helper function to recalculate values based on lastChange
  const recalculateBasedOnLastChange = useCallback(() => {
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
    const currentPaymentValue = form.getFieldValue('payment_value')
    const beneficiaryValueNum =
      Number(parseFormattedNumberInput(currentBeneficiaryValue)) || 0

    const paymentValueNum =
      Number(parseFormattedNumberInput(currentPaymentValue)) || 0

    // Helper to format beneficiary value: preserve decimal format if original has decimals
    const formatBeneficiaryValue = (value: number, originalValue: string) => {
      // If original value has decimal point (even if trailing zeros), preserve the decimal format
      if (originalValue && typeof originalValue === 'string') {
        const trimmed = originalValue.trim()
        // If original value has decimal point, always format with decimals to preserve format
        if (trimmed.includes('.')) {
          return value.toFixed(beneficiaryDecimal)
        }
        // If original value is a whole number without decimal point, keep as integer
        if (/^\d+$/.test(trimmed)) {
          return value.toString()
        }
      }
      // If value itself has decimal part, format with decimals
      if (value % 1 !== 0) {
        return value.toFixed(beneficiaryDecimal)
      }
      // Default: format with decimals to ensure consistent display
      return value.toFixed(beneficiaryDecimal)
    }

    // If lastChange is BENEFICARY and beneficiary value exists, keep it and recalculate payment
    if (lastChange === 'BENEFICARY' && beneficiaryValueNum > 0) {
      const newPaymentValue = (
        beneficiaryValueNum *
        (1 / exchangeRate)
      ).toFixed(paymentDecimal)
      form.setFieldsValue({
        beneficiary_value: formatBeneficiaryValue(
          beneficiaryValueNum,
          currentBeneficiaryValue
        ),
        payment_value: newPaymentValue,
      })
      setBeneficiaryAmount(beneficiaryValueNum)
      setPaymentAmount(Number(newPaymentValue))
      setDirection(Direction.BEN_TO_PAY)

      // Validate payment_value after setting to show error if invalid
      setTimeout(() => {
        form.validateFields(['payment_value'])
      }, 0)
    }
    // If lastChange is SENDER and payment value exists, keep it and recalculate beneficiary
    else if (lastChange === 'SENDER' && paymentValueNum > 0) {
      const newBeneficiaryValue = (paymentValueNum * exchangeRate).toFixed(
        beneficiaryDecimal
      )
      const newBeneficiaryValueNum = Number(newBeneficiaryValue)

      // Check if recalculated beneficiary_value exceeds max limit
      const maxLimit = getMaxLimit(beneficiaryCurrency)

      // If user is currently typing payment_value, don't format it - keep the raw value
      const paymentValueToSet = isTypingPaymentValueRef.current
        ? currentPaymentValue
        : paymentValueNum.toFixed(paymentDecimal)

      if (maxLimit !== null && newBeneficiaryValueNum > maxLimit) {
        // Set flag to prevent further recalculation
        isExceedingMaxLimitRef.current = true
        // Set the calculated value, don't clear payment_value
        form.setFieldsValue({
          beneficiary_value: newBeneficiaryValue,
          payment_value: paymentValueToSet,
        })
        setBeneficiaryAmount(newBeneficiaryValueNum)
        setPaymentAmount(paymentValueNum)
        setDirection(Direction.PAY_TO_BEN)
        // Only trigger validation to show error, don't change values
        setTimeout(() => {
          form.validateFields(['beneficiary_value']).catch(() => {
            // Validation errors are expected
          })
        }, 0)
      } else {
        // Reset flag if value is within limit
        isExceedingMaxLimitRef.current = false
        form.setFieldsValue({
          beneficiary_value: newBeneficiaryValue,
          payment_value: paymentValueToSet,
        })
        setBeneficiaryAmount(newBeneficiaryValueNum)
        setPaymentAmount(paymentValueNum)
        setDirection(Direction.PAY_TO_BEN)

        // Validate both fields after setting to show errors if invalid
        setTimeout(() => {
          form.validateFields(['beneficiary_value', 'payment_value'])
        }, 0)
      }
    }
    // If both values exist, use direction to determine which to keep
    else if (beneficiaryValueNum > 0 && paymentValueNum > 0) {
      if (direction === Direction.BEN_TO_PAY) {
        const newPaymentValue = (
          beneficiaryValueNum *
          (1 / exchangeRate)
        ).toFixed(paymentDecimal)

        // If user is currently typing beneficiary_value, keep the raw value
        const beneficiaryValueToSet = isTypingBeneficiaryValueRef.current
          ? currentBeneficiaryValue
          : formatBeneficiaryValue(beneficiaryValueNum, currentBeneficiaryValue)
        form.setFieldsValue({
          beneficiary_value: beneficiaryValueToSet,
          payment_value: newPaymentValue,
        })
        setBeneficiaryAmount(beneficiaryValueNum)
        setPaymentAmount(Number(newPaymentValue))

        // Validate payment_value after setting to show error if invalid
        setTimeout(() => {
          form.validateFields(['payment_value'])
        }, 0)
      } else {
        const newBeneficiaryValue = (paymentValueNum * exchangeRate).toFixed(
          beneficiaryDecimal
        )
        const newBeneficiaryValueNum = Number(newBeneficiaryValue)

        // Check if recalculated beneficiary_value exceeds max limit
        const maxLimit = getMaxLimit(beneficiaryCurrency)

        // If user is currently typing payment_value, don't format it - keep the raw value
        const paymentValueToSet = isTypingPaymentValueRef.current
          ? currentPaymentValue
          : paymentValueNum.toFixed(paymentDecimal)

        if (maxLimit !== null && newBeneficiaryValueNum > maxLimit) {
          // Set flag to prevent further recalculation
          isExceedingMaxLimitRef.current = true
          // Set the calculated value, don't clear payment_value
          form.setFieldsValue({
            beneficiary_value: newBeneficiaryValue,
            payment_value: paymentValueToSet,
          })
          setBeneficiaryAmount(newBeneficiaryValueNum)
          setPaymentAmount(paymentValueNum)
          // Only trigger validation to show error, don't change values
          setTimeout(() => {
            form.validateFields(['beneficiary_value']).catch(() => {
              // Validation errors are expected
            })
          }, 0)
        } else {
          // Reset flag if value is within limit
          isExceedingMaxLimitRef.current = false
          form.setFieldsValue({
            beneficiary_value: newBeneficiaryValue,
            payment_value: paymentValueToSet,
          })
          setBeneficiaryAmount(newBeneficiaryValueNum)
          setPaymentAmount(paymentValueNum)

          // Validate both fields after setting to show errors if invalid
          setTimeout(() => {
            form.validateFields(['beneficiary_value', 'payment_value'])
          }, 0)
        }
      }
    }
  }, [
    exchangeRate,
    paymentDecimal,
    beneficiaryDecimal,
    form,
    beneficiaryCurrency,
  ])

  useEffect(() => {
    if (loadingRate || !exchangeRate) return

    // Don't recalculate if we're setting default values
    if (isSettingDefaultRef.current) return

    // Don't recalculate during initial setup (before defaults have been set)
    if (!hasSetDefaultRef.current) return

    // Don't recalculate during first calculation phase
    if (firstCalculate) return

    // Don't recalculate if beneficiary_value exceeds max limit
    if (isExceedingMaxLimitRef.current) return

    // Don't recalculate if we're changing currency (format is already handled in handleChange functions)
    if (isChangingCurrencyRef.current) return

    // If currencies are the same, exchangeRate = 1, and values are already correct
    // No need to recalculate to avoid "flickering"
    const isSameCurrency = beneficiaryCurrency === paymentCurrency
    if (isSameCurrency) return

    // Read current values from form to avoid dependency on state
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
    const currentPaymentValue = form.getFieldValue('payment_value')
    const beneficiaryValueNum =
      Number(parseFormattedNumberInput(currentBeneficiaryValue)) || 0

    const paymentValueNum =
      Number(parseFormattedNumberInput(currentPaymentValue)) || 0

    // If we're validating after currency change, check if exchangeRate has changed (new exchangeRate arrived)
    // Only skip if exchangeRate hasn't changed yet (still using old exchangeRate)
    const exchangeRateChanged =
      previousExchangeRateRef.current !== null &&
      previousExchangeRateRef.current !== exchangeRate
    if (
      isValidatingAfterCurrencyChangeRef.current &&
      beneficiaryValueNum > 0 &&
      !exchangeRateChanged
    ) {
      // Don't update previousExchangeRate here, wait for it to change
      return
    }

    // Update previous exchangeRate
    previousExchangeRateRef.current = exchangeRate

    // Don't set default values again if both are 0 after initialization
    // Default values should only be set once during initial setup (in useEffect at line 599)
    // This prevents resetting values when exchangeRate changes after initialization
    if (paymentValueNum === 0 && beneficiaryValueNum === 0) {
      // Only recalculate if we have at least one non-zero value
      return
    } else {
      // Recalculate based on lastChange when exchange rate changes
      // This keeps the value that was just entered and recalculates the other
      // If we're changing currency, this will recalculate with new exchangeRate
      recalculateBasedOnLastChange()

      // If we're changing currency, validate after recalculation
      if (isChangingCurrencyRef.current) {
        setTimeout(async () => {
          try {
            await form.validateFields(['beneficiary_value', 'payment_value'])
            isChangingCurrencyRef.current = false
          } catch (error) {
            // Get errors to ensure they are displayed
            const beneficiaryErrors = form.getFieldError('beneficiary_value')
            const paymentErrors = form.getFieldError('payment_value')

            // Keep the flag true a bit longer to prevent onValuesChange from clearing the error
            // Then reset it so user can interact normally
            setTimeout(() => {
              isChangingCurrencyRef.current = false

              // Re-validate to ensure errors are still displayed if values are still invalid
              const currentPaymentValue = form.getFieldValue('payment_value')
              const currentBeneficiaryValue =
                form.getFieldValue('beneficiary_value')
              if (currentPaymentValue) {
                form.validateFields(['payment_value'])
              }
              if (currentBeneficiaryValue) {
                form.validateFields(['beneficiary_value'])
              }
            }, 300)
          }
        }, 100)
      }
    }

    // Reset validation flag after exchangeRate is updated and values are recalculated
    // This allows validation to run properly without waiting for fees
    if (isValidatingAfterCurrencyChangeRef.current) {
      setTimeout(() => {
        isValidatingAfterCurrencyChangeRef.current = false
        // Validate after values are recalculated with new exchangeRate
        form.validateFields(['beneficiary_value', 'payment_value'])
      }, 100)
    }
  }, [
    exchangeRate,
    loadingRate,
    beneficiaryCurrency,
    paymentCurrency,
    paymentDecimal,
    beneficiaryDecimal,
    recalculateBasedOnLastChange,
    form,
    dataAllCurrency,
    firstCalculate,
  ])

  // Set default currency and values when all required data is loaded
  useEffect(() => {
    if (!dataAllCurrency?.length || !beneficiaryCurrency) return
    const beneficiaryDefault = dataAllCurrency.find((item: any) => item.id === beneficiaryCurrency) as any;
    const beneficiaryCurrencyDefault = beneficiaryDefault ?? findCurrencyBySymbol(
      dataAllCurrency,
      'USD'
    )
    if (!beneficiaryCurrencyDefault) return

    const beneficiaryCurrencyId = beneficiaryCurrencyDefault.id
    form.setFieldsValue({
      beneficiary_currency: beneficiaryCurrencyId,
    })
    setBeneficiaryCurrency(beneficiaryCurrencyId)
    setBeneficiaryCurrencySymbol(beneficiaryCurrencyDefault.symbol)
    setBeneficiaryDecimal(beneficiaryCurrencyDefault.decimals)


    form.setFieldsValue({
      payment_currency: beneficiaryCurrencyId,
    })
    setPaymentCurrency(beneficiaryCurrencyId)
    setPaymentCurrencySymbol(beneficiaryCurrencyDefault.symbol)
    setPaymentDecimal(beneficiaryCurrencyDefault.decimals)

    // // Set default currency values if not already set
    // const currentBeneficiaryCurrency = form.getFieldValue(
    //   'beneficiary_currency'
    // )
    // const currentPaymentCurrency = form.getFieldValue('payment_currency')

    // if (!currentBeneficiaryCurrency) {
    //   form.setFieldsValue({
    //     beneficiary_currency: beneficiaryCurrencyId,
    //   })
    //   setBeneficiaryCurrency(beneficiaryCurrencyId)
    //   setBeneficiaryCurrencySymbol(beneficiaryCurrencyDefault.symbol)
    //   setBeneficiaryDecimal(beneficiaryCurrencyDefault.decimals)
    // }

    // if (!currentPaymentCurrency) {
    //   form.setFieldsValue({
    //     payment_currency: beneficiaryCurrencyId,
    //   })
    //   setPaymentCurrency(beneficiaryCurrencyId)
    //   setPaymentCurrencySymbol(beneficiaryCurrencyDefault.symbol)
    //   setPaymentDecimal(beneficiaryCurrencyDefault.decimals)
    // }

    // Wait for all required data to be loaded before setting default values
    if (!rate?.data?.data) return
    if (!paymentDecimal || paymentDecimal === 0) return
    if (!beneficiaryDecimal || beneficiaryDecimal === 0) return
    if (hasSetDefaultRef.current) return // Only set default once

    // Check if user has already entered values manually
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
    const currentPaymentValue = form.getFieldValue('payment_value')
    const beneficiaryValueNum = Number(currentBeneficiaryValue) || 0
    const paymentValueNum = Number(currentPaymentValue) || 0

    // If user has already entered values, don't set default
    if (beneficiaryValueNum > 0 || paymentValueNum > 0) {
      hasSetDefaultRef.current = true
      setLoadingDefaultValue(false)
      return
    }

    // Set default beneficiary value
    const defaultBeneficiaryValue = getDefaultCurrencyValue(
      beneficiaryCurrencyId,
      dataAllCurrency,
      false,
      '',
      firstCalculate
    )

    const paymentValue = (Number(defaultBeneficiaryValue) * (1 / rate?.data?.data)).toFixed(
      paymentDecimal
    )


    // Set direction and lastChange to prevent recalculation in other useEffect
    setDirection(Direction.BEN_TO_PAY)
    setLastChange('BENEFICARY')

    // Mark that we're setting default values
    isSettingDefaultRef.current = true
    form.setFieldsValue({
      beneficiary_value: defaultBeneficiaryValue,
      payment_value: paymentValue,
    })
    setPaymentAmount(Number(paymentValue))
    setBeneficiaryAmount(Number(defaultBeneficiaryValue))

    // Mark that default has been set and reset isSettingDefault flag
    hasSetDefaultRef.current = true
    setTimeout(() => {
      isSettingDefaultRef.current = false
      setLoadingDefaultValue(false)
    }, 0)
  }, [
    beneficiaryCurrency,
    dataAllCurrency,
    rate?.data?.data,
    paymentDecimal,
    beneficiaryDecimal,
    form,
  ])

  useEffect(() => {
    if (!beneficiaryCurrency || !paymentCurrency || created) return
    const intervalId = setInterval(() => {
      refetchRate()
    }, 60000)
    return () => clearInterval(intervalId)
  }, [beneficiaryCurrency, paymentCurrency, created])

  useEffect(() => {
    let newRequiredFiels = [...requiredFields]
    const type = (
      dataAllCurrency.find((item: any) => item.id === paymentCurrency) as any
    )?.type

    if (type === 'FIAT')
      newRequiredFiels = [...requiredFields, 'providerBankAccount']
    else newRequiredFiels = [...requiredFields, 'providerWallet']

    const values = form.getFieldsValue(newRequiredFiels)
    const allFilled = newRequiredFiels.every((field) => !!values[field])
    setIsAllFieldsFilled(allFilled)
  }, [
    paymentCurrency,
    beneficiary_value,
    payment_value,
    sender,
    companies_default,
    providerBankAccount,
    providerWallet,
  ])

  const getMaxLimit = (currency: number | undefined) => {
    const currencyETH = findCurrencyBySymbol(dataAllCurrency, 'ETH')
    const currencyUSD = findCurrencyBySymbol(dataAllCurrency, 'USD')
    const currencyUSDT = findCurrencyBySymbol(dataAllCurrency, 'USDT')
    const currencyDAI = findCurrencyBySymbol(dataAllCurrency, 'DAI')
    const currencyEUR = findCurrencyBySymbol(dataAllCurrency, 'EUR')
    const currencyHKD = findCurrencyBySymbol(dataAllCurrency, 'HKD')
    const currencyAUD = findCurrencyBySymbol(dataAllCurrency, 'AUD')
    const currencyJPY = findCurrencyBySymbol(dataAllCurrency, 'JPY')
    const currencyCHF = findCurrencyBySymbol(dataAllCurrency, 'CHF')
    const currencyCNY = findCurrencyBySymbol(dataAllCurrency, 'CNY')
    const currencyAED = findCurrencyBySymbol(dataAllCurrency, 'AED')
    const currencySGD = findCurrencyBySymbol(dataAllCurrency, 'SGD')
    const currencyGBP = findCurrencyBySymbol(dataAllCurrency, 'GBP')

    switch (currency) {
      case currencyETH?.id:
        return 10000000
      case currencyUSDT?.id:
      case currencyDAI?.id:
      case currencyUSD?.id:
      case currencyEUR?.id:
        return 10000000000
      case currencyHKD?.id:
        return 77800000000
      case currencyAUD?.id:
        return 15000000000
      case currencyJPY?.id:
        return 15479000000
      case currencyCHF?.id:
        return 8000000000
      case currencyCNY?.id:
        return 70700000000
      case currencyAED?.id:
        return 36700000000
      case currencySGD?.id:
        return 12900000000
      case currencyGBP?.id:
        return 7400000000
      default:
        return null
    }
  }

  const handleChangePaymentCurrency = async (value: number) => {
    // Mark that user has changed currency, no longer default
    isSettingDefaultRef.current = false
    // Mark that we're changing currency to prevent validation from being cleared
    isValidatingAfterCurrencyChangeRef.current = true
    // Mark that we're changing currency to prevent useEffect from recalculating
    isChangingCurrencyRef.current = true
    // Reset max limit flag when currency changes to allow recalculation with new currency
    isExceedingMaxLimitRef.current = false

    // Save current values before changing currency
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
    const currentPaymentValue = form.getFieldValue('payment_value')
    const beneficiaryValueNum = Number(parseFormattedNumberInput(currentBeneficiaryValue)) || 0
    const paymentValueNum = Number(parseFormattedNumberInput(currentPaymentValue)) || 0

    const currency = dataAllCurrency?.find(
      (item: any) => item?.id === value
    ) as any
    const newPaymentDecimal = currency?.decimals
    const newBeneficiaryDecimal = currency?.decimals

    // Update payment currency state and form field BEFORE setting values
    // This ensures validator uses the new currency when validating
    setPaymentCurrency(value)
    form.setFieldsValue({ payment_currency: value })
    setPaymentCurrencySymbol(currency?.symbol)
    setPaymentDecimal(newPaymentDecimal)

    // Update beneficiary currency to match payment currency
    // Payment and beneficiary should always use the same currency
    setBeneficiaryCurrency(value)
    form.setFieldsValue({ beneficiary_currency: value })
    setBeneficiaryCurrencySymbol(currency?.symbol)
    setBeneficiaryDecimal(newBeneficiaryDecimal)

    // Check if new currency is same as beneficiary currency (always true now)
    const isSameAsBeneficiaryCurrency = true // Always true since we set them to be the same

    // If user hasn't edited any input yet, always use current beneficiary_value as default
    if (!hasUserEditedInputRef.current) {
      // Always use current beneficiary_value from form as default value
      const beneValue = beneficiaryValueNum
        ? Number(beneficiaryValueNum).toFixed(newBeneficiaryDecimal)
        : getDefaultCurrencyValue(value, dataAllCurrency, false, '')

      const calculatedPaymentValue = exchangeRate && exchangeRate > 0
        ? (Number(beneValue) * (1 / exchangeRate)).toFixed(newPaymentDecimal)
        : Number(0).toFixed(newPaymentDecimal)
      form.setFieldsValue({
        beneficiary_value: formatNumberV2(beneValue, newBeneficiaryDecimal),
        payment_value: formatNumberV2(calculatedPaymentValue, newPaymentDecimal)
      })
      setBeneficiaryAmount(Number(beneValue))
      setPaymentAmount(Number(calculatedPaymentValue))
      setLastChange('BENEFICARY')
      setDirection(Direction.BEN_TO_PAY)

      // Skip complex logic below, continue to reset fields at the end
    } else {
      // Determine which field was edited most recently based on lastChange
      const keepBeneficiary = lastChange === 'BENEFICARY' && beneficiaryValueNum > 0
      const keepPayment = lastChange === 'SENDER' && paymentValueNum > 0

      // Priority: lastChange > direction > which value exists
      // If lastChange is BENEFICARY and value exists, keep beneficiary_value and recalculate payment_value
      if (keepBeneficiary) {
        const beneValue = beneficiaryValueNum.toFixed(newBeneficiaryDecimal)

        // Set beneficiary_value first
        form.setFieldsValue({
          beneficiary_value: beneValue,
        })
        setBeneficiaryAmount(beneficiaryValueNum)
        setDirection(Direction.BEN_TO_PAY)
        setLastChange('BENEFICARY')

        // If currencies are the same, check max limit before setting payment_value = beneficiary_value
        if (isSameAsBeneficiaryCurrency) {
          // Check max limit for payment currency
          const maxLimitPayment = getMaxLimit(value)

          // Set payment_value = beneficiary_value (even if exceeds limit, validation will show error)
          // Format payment_value with newPaymentDecimal to preserve format
          const paymentValueFormatted = beneficiaryValueNum.toFixed(newPaymentDecimal)
          form.setFieldsValue({
            payment_value: formatNumberV2(paymentValueFormatted, newPaymentDecimal)
          })
          setPaymentAmount(beneficiaryValueNum)
        }

        // Validate beneficiary_value first
        try {
          await form.validateFields(['beneficiary_value'])
          // If beneficiary_value is valid, calculate and set payment_value (if currencies are different)
          if (!isSameAsBeneficiaryCurrency) {
            const newPaymentValue = (beneficiaryValueNum * (1 / exchangeRate)).toFixed(newPaymentDecimal)
            const newPaymentValueNum = Number(newPaymentValue)

            form.setFieldsValue({
              payment_value: formatNumberV2(newPaymentValue, newPaymentDecimal)
            })
            setPaymentAmount(newPaymentValueNum)

            // Validate payment_value after setting
            setTimeout(() => {
              form.validateFields(['payment_value']).catch(() => {
                // Error is expected if payment_value has error
              })
            }, 0)
          } else {
            // Currencies are the same, payment_value already set, just validate it
            setTimeout(() => {
              form.validateFields(['payment_value']).catch(() => {
                // Error is expected if payment_value has error
              })
            }, 0)
          }

          // Validate payment_value after setting to show error if invalid
          setTimeout(async () => {
            // Keep flag true to prevent onValuesChange from clearing error
            isChangingCurrencyRef.current = true
            try {
              await form.validateFields(['payment_value'])
              isChangingCurrencyRef.current = false
            } catch (error) {
              // If payment_value has error, ensure it's displayed
              const paymentErrors = form.getFieldError('payment_value')

              // If error exists but not displayed, set it explicitly
              if (paymentErrors.length > 0) {
                // Error is already set, just ensure it's not cleared
                setTimeout(() => {
                  isChangingCurrencyRef.current = false
                }, 200)
              } else {
                // Force validation again to set error
                form.validateFields(['payment_value']).catch(() => {
                  setTimeout(() => {
                    isChangingCurrencyRef.current = false
                  }, 200)
                })
              }
            }
          }, 150)
        } catch (error) {
          // If beneficiary_value has error, clear validation of payment_value
          // If currencies are the same, payment_value should still equal beneficiary_value (even if invalid)
          // Only clear payment_value if currencies are different
          if (!isSameAsBeneficiaryCurrency) {
            form.resetFields(['payment_value'])
            form.setFields([{ name: 'payment_value', errors: [] }])
            setPaymentAmount(0)
          } else {
            // payment_value already equals beneficiary_value, clear its validation
            form.setFields([{ name: 'payment_value', errors: [] }])
            setTimeout(async () => {
              // Keep flag true to prevent onValuesChange from clearing error
              isChangingCurrencyRef.current = true
              try {
                await form.validateFields(['payment_value'])
                isChangingCurrencyRef.current = false
              } catch (error) {
                // If payment_value has error, ensure it's displayed
                const paymentErrors = form.getFieldError('payment_value')

                // Keep flag true a bit longer to prevent onValuesChange from clearing
                setTimeout(() => {
                  isChangingCurrencyRef.current = false
                }, 200)
              }
            }, 100)
          }
        }
        // Keep lastChange as BENEFICARY
      }
      // If lastChange is SENDER and value exists, keep payment_value and recalculate beneficiary_value
      else if (keepPayment) {
        const paymentValue = paymentValueNum.toFixed(newPaymentDecimal)
        // Set payment_value first
        form.setFieldsValue({
          payment_value: formatNumberV2(paymentValue, newPaymentDecimal)
        })
        setPaymentAmount(paymentValueNum)
        setDirection(Direction.PAY_TO_BEN)
        setLastChange('SENDER')

        // If currencies are the same, set beneficiary_value = payment_value
        if (isSameAsBeneficiaryCurrency) {
          const beneficiaryValueFormatted = paymentValueNum.toFixed(newBeneficiaryDecimal)
          form.setFieldsValue({
            beneficiary_value: formatNumberV2(beneficiaryValueFormatted, newBeneficiaryDecimal)
          })
          setBeneficiaryAmount(paymentValueNum)
        }

        // Validate payment_value first
        try {
          await form.validateFields(['payment_value'])
          // If payment_value is valid, calculate and set beneficiary_value (if currencies are different)
          if (!isSameAsBeneficiaryCurrency) {
            const rateToUse = exchangeRate
            const newBeneficiaryValue = (paymentValueNum * rateToUse).toFixed(beneficiaryDecimal)
            const newBeneficiaryValueNum = Number(newBeneficiaryValue)

            form.setFieldsValue({
              beneficiary_value: formatNumberV2(newBeneficiaryValue, newBeneficiaryDecimal)
            })
            setBeneficiaryAmount(newBeneficiaryValueNum)
          } else {
            // Currencies are the same, beneficiary_value already set, validate it
          }

          // Validate beneficiary_value after setting to show error if invalid
          setTimeout(async () => {
            // Keep flag true to prevent onValuesChange from clearing error
            isChangingCurrencyRef.current = true
            try {
              await form.validateFields(['beneficiary_value'])
              isChangingCurrencyRef.current = false
            } catch (error) {
              // If beneficiary_value has error, ensure it's displayed
              const beneficiaryErrors = form.getFieldError('beneficiary_value')

              // If error exists but not displayed, set it explicitly
              if (beneficiaryErrors.length > 0) {
                // Error is already set, just ensure it's not cleared
                setTimeout(() => {
                  isChangingCurrencyRef.current = false
                }, 200)
              } else {
                // Force validation again to set error
                form.validateFields(['beneficiary_value']).catch(() => {
                  setTimeout(() => {
                    isChangingCurrencyRef.current = false
                  }, 200)
                })
              }
            }
          }, 150)
        } catch (error) {
          // If payment_value has error
          // If currencies are the same, beneficiary_value should still equal payment_value (even if invalid)
          // Only clear beneficiary_value if currencies are different
          if (!isSameAsBeneficiaryCurrency) {
            form.resetFields(['beneficiary_value'])
            form.setFields([{ name: 'beneficiary_value', errors: [] }])
            setBeneficiaryAmount(0)
          } else {
            // beneficiary_value already equals payment_value, clear its validation
            form.setFields([{ name: 'beneficiary_value', errors: [] }])
            setTimeout(async () => {
              // Keep flag true to prevent onValuesChange from clearing error
              isChangingCurrencyRef.current = true
              try {
                await form.validateFields(['beneficiary_value'])
                isChangingCurrencyRef.current = false
              } catch (error) {
                // If beneficiary_value has error, ensure it's displayed
                const beneficiaryErrors = form.getFieldError('beneficiary_value')

                // Keep flag true a bit longer to prevent onValuesChange from clearing
                setTimeout(() => {
                  isChangingCurrencyRef.current = false
                }, 200)
              }
            }, 100)
          }
        }
      }
      // If both values exist but lastChange is not clear, use direction
      else if (beneficiaryValueNum > 0 && paymentValueNum > 0) {
        if (direction === Direction.BEN_TO_PAY) {
          // If currencies are the same, set payment_value = beneficiary_value
          if (isSameAsBeneficiaryCurrency) {
            const beneValue = beneficiaryValueNum.toFixed(newBeneficiaryDecimal)
            // Check max limit for payment currency
            const maxLimitPayment = getMaxLimit(value)
            // Set payment_value = beneficiary_value (even if exceeds limit, validation will show error)
            // Format payment_value with newPaymentDecimal to preserve format
            const paymentValueFormatted = beneficiaryValueNum.toFixed(newPaymentDecimal)
            form.setFieldsValue({
              beneficiary_value: formatNumberV2(beneValue, newBeneficiaryDecimal),
              payment_value: formatNumberV2(paymentValueFormatted, newPaymentDecimal)
            })
            setBeneficiaryAmount(beneficiaryValueNum)
            setPaymentAmount(beneficiaryValueNum)
            setLastChange('BENEFICARY')
            // Validate to show error if exceeds limit
            if (maxLimitPayment !== null && beneficiaryValueNum > maxLimitPayment) {
              setTimeout(() => {
                form.validateFields(['payment_value'])
              }, 0)
            }
          } else {
            const newPaymentValue = (beneficiaryValueNum * (1 / exchangeRate)).toFixed(newPaymentDecimal)
            const newPaymentValueNum = Number(newPaymentValue)

            // Check max limit for new payment currency
            const maxLimitPayment = getMaxLimit(value)
            if (maxLimitPayment !== null && newPaymentValueNum > maxLimitPayment) {
              // Set payment_value even if exceeds limit, validation will show error
              form.setFieldsValue({
                beneficiary_value: formatNumberV2(beneficiaryValueNum, newBeneficiaryDecimal),
                payment_value: formatNumberV2(newPaymentValue, newPaymentDecimal)
              })
              setBeneficiaryAmount(beneficiaryValueNum)
              setPaymentAmount(newPaymentValueNum)
              setLastChange('BENEFICARY')
              // Validate to show error
              setTimeout(() => {
                form.validateFields(['payment_value'])
              }, 0)
            } else {
              form.setFieldsValue({
                beneficiary_value: formatNumberV2(beneficiaryValueNum, newBeneficiaryDecimal),
                payment_value: formatNumberV2(newPaymentValue, newPaymentDecimal)
              })
              setBeneficiaryAmount(beneficiaryValueNum)
              setPaymentAmount(newPaymentValueNum)
              setLastChange('BENEFICARY')
            }
          }
        } else {
          // Keep payment_value and recalculate beneficiary_value
          const paymentValue = paymentValueNum.toFixed(newPaymentDecimal)
          // Set payment_value first
          form.setFieldsValue({
            payment_value: formatNumberV2(paymentValue, newPaymentDecimal)
          })
          setPaymentAmount(paymentValueNum)
          setLastChange('SENDER')

          // If currencies are the same, check max limit before setting beneficiary_value = payment_value
          if (isSameAsBeneficiaryCurrency) {
            // Check max limit for beneficiary currency
            const maxLimitBeneficiary = getMaxLimit(value)

            // Set beneficiary_value = payment_value (even if exceeds limit, validation will show error)
            // Format beneficiary_value with newBeneficiaryDecimal to preserve format
            const beneficiaryValueFormatted = paymentValueNum.toFixed(newBeneficiaryDecimal)
            form.setFieldsValue({
              beneficiary_value: formatNumberV2(beneficiaryValueFormatted, newBeneficiaryDecimal)
            })
            setBeneficiaryAmount(paymentValueNum)

            // If exceeds max limit, validate to show error immediately
            if (maxLimitBeneficiary !== null && paymentValueNum > maxLimitBeneficiary) {
              // Error will be shown by validation below
            }
          }

          // Validate payment_value first
          try {
            await form.validateFields(['payment_value'])
            // If payment_value is valid, calculate and set beneficiary_value (if currencies are different)
            if (!isSameAsBeneficiaryCurrency) {
              const rateToUse = exchangeRate
              const newBeneficiaryValue = (paymentValueNum * rateToUse).toFixed(beneficiaryDecimal)
              const newBeneficiaryValueNum = Number(newBeneficiaryValue)

              // Check max limit for beneficiary currency
              const maxLimitBeneficiary = getMaxLimit(beneficiaryCurrency)

              form.setFieldsValue({
                beneficiary_value: formatNumberV2(newBeneficiaryValue, newBeneficiaryDecimal)
              })
              setBeneficiaryAmount(newBeneficiaryValueNum)
            } else {
              // Currencies are the same, beneficiary_value already set, validate it
            }

            // Validate beneficiary_value after setting to show error if invalid
            setTimeout(async () => {
              // Keep flag true to prevent onValuesChange from clearing error
              isChangingCurrencyRef.current = true
              try {
                await form.validateFields(['beneficiary_value'])
                isChangingCurrencyRef.current = false
              } catch (error) {
                // If beneficiary_value has error, ensure it's displayed
                const beneficiaryErrors = form.getFieldError('beneficiary_value')

                // If error exists but not displayed, set it explicitly
                if (beneficiaryErrors.length > 0) {
                  // Error is already set, just ensure it's not cleared
                  setTimeout(() => {
                    isChangingCurrencyRef.current = false
                  }, 200)
                } else {
                  // Force validation again to set error
                  form.validateFields(['beneficiary_value']).catch(() => {
                    setTimeout(() => {
                      isChangingCurrencyRef.current = false
                    }, 200)
                  })
                }
              }
            }, 150)
          } catch (error) {
            // If payment_value has error
            // If currencies are the same, beneficiary_value should still equal payment_value (even if invalid)
            // Only clear beneficiary_value if currencies are different
            if (!isSameAsBeneficiaryCurrency) {
              form.resetFields(['beneficiary_value'])
              form.setFields([{ name: 'beneficiary_value', errors: [] }])
              setBeneficiaryAmount(0)
            } else {
              // beneficiary_value already equals payment_value, clear its validation
              form.setFields([{ name: 'beneficiary_value', errors: [] }])
            }
          }
        }
      }
      // If no value exists or both are 0, or user hasn't edited, set default value
      else {
        // Check if should set default value: user hasn't edited OR value is empty/0
        const shouldSetDefault = !hasUserEditedInputRef.current ||
          !beneficiaryValueNum ||
          beneficiaryValueNum === 0

        if (shouldSetDefault) {
          // Always use current beneficiary_value from form as default value
          const beneValue = beneficiaryValueNum
            ? Number(beneficiaryValueNum).toFixed(newBeneficiaryDecimal)
            : getDefaultCurrencyValue(value, dataAllCurrency, false, '')

          const calculatedPaymentValue = exchangeRate && exchangeRate > 0
            ? (Number(beneValue) * (1 / exchangeRate)).toFixed(newPaymentDecimal)
            : Number(0).toFixed(newPaymentDecimal)
          form.setFieldsValue({
            beneficiary_value: formatNumberV2(beneValue, newBeneficiaryDecimal),
            payment_value: formatNumberV2(calculatedPaymentValue, newPaymentDecimal)
          })
          setBeneficiaryAmount(Number(beneValue))
          setPaymentAmount(Number(calculatedPaymentValue))
          setLastChange('BENEFICARY')
          setDirection(Direction.BEN_TO_PAY)
        } else {
          // User has edited, use current beneficiary_value from form
          const val = form.getFieldValue('beneficiary_value')
          const hasExistingValue = val && Number(val) > 0
          // Use current value from form if exists, otherwise use default
          const beneValue = hasExistingValue
            ? Number(val).toFixed(newBeneficiaryDecimal)
            : getDefaultCurrencyValue(value, dataAllCurrency, false, '')

          const calculatedPaymentValue = exchangeRate && exchangeRate > 0
            ? (Number(beneValue) * (1 / exchangeRate)).toFixed(newPaymentDecimal)
            : Number(0).toFixed(newPaymentDecimal)
          form.setFieldsValue({
            beneficiary_value: formatNumberV2(beneValue, newBeneficiaryDecimal),
            payment_value: formatNumberV2(calculatedPaymentValue, newPaymentDecimal)
          })
          setBeneficiaryAmount(Number(beneValue))
          setPaymentAmount(Number(calculatedPaymentValue))
          setLastChange('BENEFICARY')
          setDirection(Direction.BEN_TO_PAY)
          if (hasExistingValue) {
            setTimeout(() => {
              form.validateFields(['beneficiary_value', 'payment_value'])
            }, 0)
          }
        }
      }
    }

    setSenderSelected(null)
    setCompanyDefaultSelected(null)
    setWalletSelectedProvider(null)
    setBankAccountSelectedProvider(null)
    form.resetFields([
      'sender',
      'companies_default',
      'providerBankAccount',
      'providerWallet',
      'supportDocument',
    ])
    setDocument({ file: {} as UploadFile<any>, fileList: [] })

    // Validate field that was kept after currency change
    // Skip validation if user hasn't edited (will be validated after exchangeRate is updated)
    if (hasUserEditedInputRef.current) {
      setTimeout(async () => {
        // Determine which field was kept
        const keepBeneficiary = lastChange === 'BENEFICARY' && beneficiaryValueNum > 0
        const keepPayment = lastChange === 'SENDER' && paymentValueNum > 0

        // Validate the field that was kept first
        if (keepBeneficiary) {
          try {
            await form.validateFields(['beneficiary_value'])
            // If beneficiary_value is valid, validate payment_value
            try {
              await form.validateFields(['payment_value'])
            } catch (paymentError) {
              const paymentErrors = form.getFieldError('payment_value')
              // Payment value has errors, ensure they are displayed
            }
          } catch (beneficiaryError) {
            const beneficiaryErrors = form.getFieldError('beneficiary_value')
            // If beneficiary_value has errors, payment_value is already cleared, no need to validate it
          }
        } else if (keepPayment) {
          try {
            await form.validateFields(['payment_value'])
            // If payment_value is valid, validate beneficiary_value
            try {
              await form.validateFields(['beneficiary_value'])
            } catch (beneficiaryError) {
              const beneficiaryErrors = form.getFieldError('beneficiary_value')
              // Beneficiary value has errors, ensure they are displayed
            }
          } catch (paymentError) {
            const paymentErrors = form.getFieldError('payment_value')
            // If payment_value has errors, beneficiary_value is already cleared, no need to validate it
          }
        }

        // Reset flag after validation completes, but ensure errors persist
        setTimeout(() => {
          isChangingCurrencyRef.current = false

          // Re-validate to ensure errors are still displayed if values are still invalid
          const currentPaymentValue = form.getFieldValue('payment_value')
          const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
          if (currentPaymentValue) {
            form.validateFields(['payment_value'])
          }
          if (currentBeneficiaryValue) {
            form.validateFields(['beneficiary_value'])
          }
        }, 50)
      }, 100)
    } else {
      // User hasn't edited, reset flag immediately (validation will happen after exchangeRate is updated)
      setTimeout(() => {
        isChangingCurrencyRef.current = false
        isValidatingAfterCurrencyChangeRef.current = false
      }, 100)
    }
  }

  const handleChangePaymentValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Mark that user has manually entered, no longer default
    isSettingDefaultRef.current = false
    hasUserEditedInputRef.current = true
    isTypingPaymentValueRef.current = true
    setLastChange('SENDER')
    setDirection(Direction.PAY_TO_BEN)
    form.setFieldValue(
      'payment_value',
      formatNumberOnChangeInput(e.target.value, paymentDecimal)
    )
  }

  const handleBlurPayment = async () => {
    isTypingPaymentValueRef.current = false
    const raw = form.getFieldValue('payment_value')
    const val = Number(parseFormattedNumberInput(raw))
    if (val && val > 0) {
      const maxLimit = getMaxLimit(paymentCurrency)
      if (maxLimit !== null && val > maxLimit) {
        form.resetFields(['beneficiary_value'])
        setBeneficiaryAmount(0)
        form.validateFields(['beneficiary_value'])
        return
      }

      setPaymentAmount(val)
      form.setFieldsValue({
        beneficiary_value: formatNumberV2(val, beneficiaryDecimal),
        payment_value: formatNumberV2(val, paymentDecimal),
      })
      setBeneficiaryAmount(val)
      setDirection(Direction.PAY_TO_BEN)
      setLastChange('SENDER')
      form.validateFields(['beneficiary_value'])
    } else {
      form.resetFields(['beneficiary_value'])
      setPaymentAmount(0)
      setBeneficiaryAmount(0)
      form.validateFields(['beneficiary_value'])
    }
  }

  useEffect(() => {
    if (loadingCompanyCurrency || companyDefaultCurrency === null) return
    if (companyDefaultCurrency?.length) {
      form.setFields([{ name: 'payment_currency', errors: [] }])
      setDisabledProvider(false)
    } else {
      form.setFields([
        {
          name: 'payment_currency',
          errors: ['No provider supports this currency'],
        },
      ])
      setDisabledProvider(true)
    }
  }, [companyDefaultCurrency, loadingCompanyCurrency, paymentCurrency, form])

  useEffect(() => {
    if (
      Array.isArray(dataListSender?.items) &&
      dataListSender?.items.length > 0
    ) {
      if (form?.getFieldValue('sender')) {
        const selected = dataListSender?.items?.find(
          (item: any) => item.id === form?.getFieldValue('sender')
        )
        setSenderSelected(selected)
      }
      setListSender(dataListSender?.items)
    }
  }, [dataListSender])

  useEffect(() => {
    if (
      Array.isArray(dataListWalletProvider?.items) &&
      dataListWalletProvider?.items.length > 0
    )
      setListWalletProvider(dataListWalletProvider?.items)
    else setListWalletProvider([])
  }, [dataListWalletProvider])

  useEffect(() => {
    if (
      Array.isArray(dataListBankAccountProvider?.items) &&
      dataListBankAccountProvider?.items.length > 0
    )
      setListBankAccountProvider(dataListBankAccountProvider?.items)
    else setListBankAccountProvider([])
  }, [dataListBankAccountProvider])

  useEffect(() => {
    if (loadingFee || transferFee === null || serviceFee === null) return

    if (paymentAmount > 0 && beneficiaryAmount > 0) {
      if (checked === listPayFee[0].value) {
        const minPayment = roundTo(
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        )

        const minBeneficiary = roundTo(
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
          beneficiaryDecimal
        )
        if (paymentAmount < minPayment) {
          // Since currencies are the same, payment_value = beneficiary_value
          // Set error on payment_value instead of beneficiary_value
          const errorMessage = `You get must be at least ${formatNumber(minBeneficiary, beneficiaryDecimal)}`
          form.setFields([
            {
              name: 'payment_value',
              errors: [errorMessage],
            },
          ])
        } else {
          // Only clear min amount errors, keep other validation errors (like max limit)
          const currentErrors = form.getFieldError('payment_value')
          // Filter out min amount errors, keep other errors
          const otherErrors = currentErrors.filter(
            (err: string) => !err?.includes('must be at least')
          )
          if (otherErrors.length === 0) {
            // No other errors, clear all
            form.setFields([{ name: 'payment_value', errors: [] }])
          } else {
            // Keep other errors (like max limit)
            form.setFields([{ name: 'payment_value', errors: otherErrors }])
          }
        }
      } else {
        // const minBeneficiary = roundTo(
        //   transferFee +
        //   serviceFee +
        //   configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
        //   beneficiaryDecimal
        // )
        const valData = Number(
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate
        )
        const minBeneficiary = roundTo(valData, beneficiaryDecimal)

        // Since currencies are the same, paymentAmount = beneficiaryAmount
        // Check paymentAmount instead and set error on payment_value
        if (paymentAmount < minBeneficiary) {
          const errorMessage = `You get must be at least ${formatNumber(minBeneficiary, beneficiaryDecimal)}`
          form.setFields([
            {
              name: 'payment_value',
              errors: [errorMessage],
            },
          ])
        } else {
          // Only clear min amount errors, keep other validation errors (like max limit)
          const currentErrors = form.getFieldError('payment_value')
          // Filter out min amount errors, keep other errors
          const otherErrors = currentErrors.filter(
            (err: string) => !err?.includes('must be at least')
          )
          if (otherErrors.length === 0) {
            // No other errors, clear all
            form.setFields([{ name: 'payment_value', errors: [] }])
          } else {
            // Keep other errors (like max limit)
            form.setFields([{ name: 'payment_value', errors: otherErrors }])
          }
        }
      }
    }
  }, [
    paymentAmount,
    beneficiaryAmount,
    transferFee,
    serviceFee,
    checked,
    loadingFee,
    configFee,
    beneficiaryCurrency,
    exchangeRate,
    paymentDecimal,
    beneficiaryDecimal,
  ])

  // Reset validation flag when fees are loaded (backup reset in case exchangeRate useEffect didn't reset it)
  useEffect(() => {
    // Only reset flag if it's currently set and fees are ready
    // This is a backup reset in case exchangeRate useEffect didn't reset it
    if (
      isValidatingAfterCurrencyChangeRef.current &&
      !loadingFee &&
      transferFee !== null &&
      serviceFee !== null
    ) {
      // Wait a bit more to ensure all fee-related useEffect have run
      const timeoutId = setTimeout(() => {
        // Only reset if still set (might have been reset by exchangeRate useEffect)
        if (isValidatingAfterCurrencyChangeRef.current) {
          isValidatingAfterCurrencyChangeRef.current = false
        }
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [loadingFee, transferFee, serviceFee])

  // Re-validate beneficiary_value after fee is fetched to ensure validation uses correct fee data
  // This is specifically to handle the case when beneficiaryCurrency changes and fees are recalculated
  useEffect(() => {
    if (!configFee || loadingFee) {
      return
    }

    // If beneficiary pays fee, wait for transferFee and serviceFee to be calculated
    if (checked === listPayFee[1].value) {
      if (transferFee === null || serviceFee === null) {
        return
      }
    }

    const beneficiaryValue = form.getFieldValue('beneficiary_value')

    if (beneficiaryValue && Number(beneficiaryValue) > 0) {
      // Use setTimeout to ensure state is updated and avoid conflicts with other validations
      // Increased timeout to 200ms to ensure currency change operations have completed
      setTimeout(() => {
        form
          .validateFields(['beneficiary_value'])
          .then(() => {
            // Validation passed with new fees
          })
          .catch(() => {
            // Error is expected if value is invalid, just ensure validation runs
          })
      }, 200)
    }
  }, [
    configFee,
    loadingFee,
    beneficiaryCurrency,
    transferFee,
    serviceFee,
    checked,
    form,
  ])

  useEffect(() => {
    if (!configFee || loadingFee) return

    if (checked === listPayFee[0].value) {
      switch (configFee?.serviceFeeType) {
        case 'PERCENTAGE': {
          if (!beneficiaryAmount) setServiceFee(null)
          else
            setServiceFee(
              Number(
                roundUp(
                  ((beneficiaryAmount || 0) * configFee?.serviceFee) /
                  100 /
                  exchangeRate,
                  paymentDecimal
                )
              )
            )
          break
        }
        case 'FIXED':
          setServiceFee(
            Number(
              roundUp(
                (configFee?.serviceFee * configFee?.usdToFeeCurrencyRate) /
                exchangeRate,
                paymentDecimal
              )
            )
          )
          break
      }
    } else {
      switch (configFee?.serviceFeeType) {
        case 'PERCENTAGE': {
          if (!beneficiaryAmount) setServiceFee(null)
          else
            setServiceFee(
              Number(
                roundUp(
                  ((beneficiaryAmount || 0) * configFee?.serviceFee) / 100,
                  beneficiaryDecimal
                )
              )
            )
          break
        }
        case 'FIXED':
          setServiceFee(
            Number(
              roundUp(
                configFee?.serviceFee * configFee?.usdToFeeCurrencyRate,
                beneficiaryDecimal
              )
            )
          )
          break
      }
    }
  }, [
    configFee,
    checked,
    paymentAmount,
    beneficiaryAmount,
    exchangeRate,
    loadingFee,
    beneficiaryCurrency,
    paymentDecimal,
    beneficiaryDecimal,
  ])

  const handleTotalTopay = () => {
    if (checked === listPayFee[0].value) {
      if (transferFee === null) return `-`
      if (
        roundTo(
          paymentAmount -
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        ) < 0
      )
        return `-`
      return `${formatNumber(
        (paymentAmount + transferFee + serviceFee).toFixed(paymentDecimal),
        paymentDecimal
      )} ${paymentCurrencySymbol}`
    } else {
      if (
        roundTo(
          beneficiaryAmount -
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
          beneficiaryDecimal
        ) < 0
      )
        return `-`
      else
        return `${formatNumber(
          paymentAmount.toFixed(paymentDecimal),
          paymentDecimal
        )} ${paymentCurrencySymbol}`
    }
  }

  const handleBeneficiaryGet = () => {
    if (checked === listPayFee[0].value) {
      if (
        roundTo(
          paymentAmount -
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        ) < 0
      )
        return `-`
      return `${formatNumber(
        Number(beneficiaryAmount.toFixed(beneficiaryDecimal)),
        beneficiaryDecimal
      )} ${beneficiaryCurrencySymbol}`
    } else {
      if (transferFee === null) return `-`
      if (
        roundTo(
          beneficiaryAmount -
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
          beneficiaryDecimal
        ) < 0
      )
        return `-`
      else
        return `${formatNumber(
          Number(
            (beneficiaryAmount - transferFee - serviceFee).toFixed(
              beneficiaryDecimal
            )
          ),
          beneficiaryDecimal
        )} ${beneficiaryCurrencySymbol}`
    }
  }

  const isNegativeBeneficiaryReceive = () => {
    if (checked === listPayFee[0].value) {
      // For payer-fee option, "You Will Receive" shows beneficiaryAmount only
      // when buffer condition passes, so negative display should not occur.
      return (
        roundTo(
          paymentAmount -
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        ) < 0
      )
    }
    if (transferFee === null) return false
    // For the second option, displayed "You Will Receive" is:
    // beneficiaryAmount - transferFee - serviceFee
    // Disable when that displayed value would be negative.
    return (
      roundTo(
        beneficiaryAmount - transferFee - serviceFee,
        beneficiaryDecimal
      ) < 0
    )
  }

  return (
    <Form
      form={form}
      onFinish={onSubmitOrder}
      onValuesChange={(changedValues, allValues) => {
        // Safety check: ensure changedValues has at least one key
        const changedKeys = Object.keys(changedValues)
        if (changedKeys.length === 0) {
          return
        }

        const changedFieldName = changedKeys[0]
        const changedValue = changedValues[changedFieldName]

        // Don't clear validation errors when changing currency
        if (isChangingCurrencyRef.current) {
          return
        }

        if (
          changedFieldName === 'payment_value' ||
          changedFieldName === 'beneficiary_value'
        ) {
          if (isValidatingAfterCurrencyChangeRef.current) {
            return // Skip clearing validation when changing currency
          }
        }

        // Don't clear payment_currency error if it's "No provider supports this currency"
        const paymentCurrencyErrors = form.getFieldError('payment_currency')
        const hasProviderError =
          paymentCurrencyErrors &&
          Array.isArray(paymentCurrencyErrors) &&
          paymentCurrencyErrors.some((err: string) =>
            err?.includes('No provider supports this currency')
          )

        // If payment_currency has provider error, don't clear it when changing other fields
        if (hasProviderError && changedFieldName !== 'payment_currency') {
          // Only clear errors for fields that are not payment_currency
          const fieldsToClear: string[] = []
          Object.keys(changedValues).forEach((fieldName) => {
            if (fieldName !== 'payment_currency') {
              fieldsToClear.push(fieldName)
            }
          })
          if (fieldsToClear.length > 0) {
            const filteredChangedValues: any = {}
            fieldsToClear.forEach((fieldName) => {
              filteredChangedValues[fieldName] = changedValues[fieldName]
            })
            clearFieldErrorMessageOnChange(form)(filteredChangedValues)
          }
          return
        }

        clearFieldErrorMessageOnChange(form)(changedValues)
      }}
      initialValues={{
        send_date: dayjs(),
        special_intructions: specialIntructionsDraft,
      }}>
      <Row gutter={{ md: 40 }}>
        <Col xs={24} md={14}>
          <div
            style={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
            <div className='request-money-frame-detail'>
              <Typography className='title-detail'>Transfer Details</Typography>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className='input-select'>
                  <Form.Item
                    label='You get'
                    name={'payment_value'}
                    style={{ width: '60%' }}
                    validateTrigger='onBlur'
                    rules={[
                      { required: true, message: 'This field is required' },
                      {
                        validator: async (_, value) => {
                          if (!value) return Promise.resolve()

                          const rawValue = parseFormattedNumberInput(value)
                          if (
                            value === '.' ||
                            (rawValue.endsWith('.') && !rawValue.startsWith('.'))
                          ) {
                            return Promise.resolve()
                          }

                          const numValue = Number(rawValue)
                          if (Number.isNaN(numValue) || numValue <= 0) {
                            return Promise.reject(
                              new Error('The amount must be greater than 0')
                            )
                          }

                          // Max limit check theo currency - use form field value to ensure we use the latest currency
                          const currentPaymentCurrency =
                            form.getFieldValue('payment_currency') ||
                            paymentCurrency
                          const maxLimit = getMaxLimit(currentPaymentCurrency)

                          if (maxLimit !== null && numValue > maxLimit) {
                            return Promise.reject(
                              new Error(
                                `Amount exceeds the maximum allowed limit of ${formatNumber(
                                  maxLimit,
                                  paymentDecimal
                                )}`
                              )
                            )
                          }

                          // Min amount check - "You get must be at least"
                          // Since payment and beneficiary currencies are the same, payment_value = beneficiary_value
                          // Skip min amount validation if configFee is not available or loading
                          if (!configFee || loadingFee) {
                            return Promise.resolve()
                          }

                          // If transferFee or serviceFee is null, skip validation to avoid false positives
                          if (transferFee === null || serviceFee === null) {
                            return Promise.resolve()
                          }

                          // case: sender pay fee
                          if (checked === listPayFee[0].value) {
                            const minBeneficiary = roundTo(
                              configFee?.bufferUsd *
                              configFee?.usdToFeeCurrencyRate,
                              paymentDecimal
                            )
                            // Since currencies are the same, payment_value should equal beneficiary_value
                            // So we check if payment_value meets the minimum beneficiary requirement
                            if (numValue < minBeneficiary) {
                              const errorMsg = `You get must be at least ${formatNumber(minBeneficiary, paymentDecimal)}`
                              return Promise.reject(new Error(errorMsg))
                            }
                          }
                          // case: beneficiary pay fee
                          else if (checked === listPayFee[1].value) {
                            const minBeneficiary = roundTo(
                              configFee?.bufferUsd *
                              configFee?.usdToFeeCurrencyRate,
                              paymentDecimal
                            )
                            // Since currencies are the same, payment_value should equal beneficiary_value
                            // So we check if payment_value meets the minimum beneficiary requirement
                            if (numValue < minBeneficiary) {
                              const errorMsg = `You get must be at least ${formatNumber(minBeneficiary, paymentDecimal)}`
                              return Promise.reject(new Error(errorMsg))
                            }
                          }

                          return Promise.resolve()
                        },
                      },
                    ]}>
                    <Input
                      onChange={handleChangePaymentValue}
                      onBlur={handleBlurPayment}
                      onKeyDown={(e) => {
                        const allowedKeys = [
                          'Backspace',
                          'Delete',
                          'ArrowLeft',
                          'ArrowRight',
                          'Tab',
                        ]
                        if (allowedKeys?.includes(e.key)) return

                        // Only allow numbers or decimal point
                        if (!/^[0-9.]$/.test(e.key)) {
                          e.preventDefault()
                          return
                        }

                        const { value, selectionStart, selectionEnd } =
                          e.currentTarget

                        // Only allow one decimal point
                        if (e.key === '.' && value?.includes('.')) {
                          e.preventDefault()
                          return
                        }

                        // Check number of decimal places
                        if (value?.includes('.')) {
                          const dotIndex = value?.indexOf('.')
                          const decimals = value?.substring(dotIndex + 1)

                          // If typing after decimal point, check length
                          if (
                            selectionStart !== null &&
                            selectionStart > dotIndex &&
                            decimals.length >= paymentDecimal
                          ) {
                            e.preventDefault()
                          }
                        }
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={'payment_currency'}
                    style={{
                      width: '40%',
                      marginTop: isTablet ? '22px' : '32px',
                    }}
                    validateTrigger='onChange'
                    rules={[
                      {
                        validator: async (_, value) => {
                          if (!value) return Promise.resolve()

                          // Check if provider supports this currency
                          if (
                            loadingCompanyCurrency ||
                            companyDefaultCurrency === null
                          ) {
                            return Promise.resolve()
                          }

                          if (!companyDefaultCurrency?.length) {
                            setDisabledProvider(true)
                            return Promise.reject(
                              new Error('No provider supports this currency')
                            )
                          }

                          setDisabledProvider(false)
                          return Promise.resolve()
                        },
                      },
                    ]}>
                    <Select
                      suffixIcon={<ArrowIcon />}
                      popupClassName='custom-select-dropdown'
                      onChange={(value) => handleChangePaymentCurrency(value)}
                      className='select-send-money'>
                      <OptGroup key='Fiat' label='Fiat'>
                        {dataAllCurrency.map((item: any) => {
                          if (item?.type === 'FIAT')
                            return (
                              <Option key={item.id} value={item.id}>
                                <div className='currency-option'>
                                  <img
                                    src={`${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`}
                                    onError={(e) => {
                                      e.currentTarget.src = smartLogoDefault
                                    }}
                                    style={{
                                      objectFit: 'cover',
                                      width: isTablet ? 24 : 32,
                                      height: isTablet ? 24 : 32,
                                      borderRadius: '50%',
                                    }}
                                    alt='currency'
                                  />
                                  <span>{item.symbol}</span>
                                </div>
                                {paymentCurrency === item.id && <CheckIcon />}
                              </Option>
                            )
                        })}
                      </OptGroup>
                      <OptGroup key='Crypto' label='Crypto'>
                        {dataAllCurrency.map((item: any) => {
                          if (item?.type === 'CRYPTO')
                            return (
                              <Option key={item.id} value={item.id}>
                                <div className='currency-option'>
                                  <img
                                    src={`${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`}
                                    onError={(e) => {
                                      e.currentTarget.src = smartLogoDefault
                                    }}
                                    style={{
                                      objectFit: 'cover',
                                      width: isTablet ? 24 : 32,
                                      height: isTablet ? 24 : 32,
                                      borderRadius: '50%',
                                    }}
                                    alt='currency'
                                  />
                                  <span>{item.symbol}</span>
                                </div>
                                {paymentCurrency === item.id && <CheckIcon />}
                              </Option>
                            )
                        })}
                      </OptGroup>
                    </Select>
                  </Form.Item>
                </div>
              </div>
              <SelectSender
                isTablet={isTablet}
                listSender={listSender}
                paramsSender={paramsSender}
                setParamsSender={setParamsSender}
                senderSelected={senderSelected}
                setSenderSelected={setSenderSelected}
                setAddSender={setAddSender}
              />
              <div className='input-recipient'>
                <div style={{ width: '100%' }}>
                  <Form.Item
                    style={{ width: '100%' }}
                    label='Purpose of Remittance'
                    name='purposeOfRemittance'
                    validateTrigger='onBlur'
                    rules={[
                      { required: true, message: 'This field is required' },
                    ]}>
                    <Select
                      className='select-recipient'
                      popupClassName='select-beneficiary'
                      placeholder='Purpose of Remitter'
                      suffixIcon={<ArrowIcon />}
                      optionLabelProp='label'
                      onChange={(value) => setPurpose(value)}>
                      {optionPurpostOfRemitter.map((item: any) => (
                        <Option
                          key={item.value}
                          value={item.value}
                          label={item.label}>
                          <span style={{ fontWeight: 400, fontSize: '14px' }}>
                            {item?.label}
                          </span>
                          {purpose === item?.value && <CheckSelectIcon />}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    style={{ width: '100%', marginTop: '32px' }}
                    label='Special instructions'
                    name='special_intructions'
                    validateTrigger='onBlur'
                    rules={[
                      {
                        max: 256,
                        message: 'Maximum 256 characters',
                        validateTrigger: 'onBlur',
                      },
                    ]}>
                    <TextArea
                      className='sendmoney-address'
                      placeholder='Enter a note'
                      autoSize={{ minRows: 1, maxRows: 5 }}
                      onChange={(e) => {
                        const currentValue = e.target.value
                        const idUserPrefix = specialIntructionsDraft || ''
                        if (!currentValue.startsWith(idUserPrefix)) {
                          const finalValue = idUserPrefix
                          form.setFieldValue('special_intructions', finalValue)
                        } else {
                          form.setFieldValue(
                            'special_intructions',
                            currentValue
                          )
                        }
                      }}
                    />
                  </Form.Item>
                </div>
              </div>
              <div>
                <Form.Item label='Supporting Document' name='supportDocument'>
                  <Upload
                    disabled={document?.fileList.length === 5}
                    multiple
                    customRequest={({ file, onSuccess, onError }) => {
                      const formData = new FormData()
                      formData.append('file', file as RcFile)

                      fetch(`${ROOT_URL}/api/v1/storage/upload`, {
                        method: 'POST',
                        headers: {
                          Authorization: `Bearer ${localStorage.getItem(
                            TOKEN_KEY
                          )}`,
                        },
                        body: formData,
                      })
                        .then((res) => res.text())
                        .then((resString) => {
                          onSuccess?.(resString)
                        })
                        .catch((err) => onError?.(err))
                    }}
                    showUploadList={false}
                    fileList={document?.fileList}
                    onChange={(file) => handleChangeFile(file)}
                    accept={acceptUploadFileTypes}
                    beforeUpload={beforeUpload}>
                    <div className='upload-file'>
                      <img
                        src={
                          document?.fileList.length === 5
                            ? CloudArrowUpDisabled
                            : CloudArrowUp
                        }
                        style={{ width: 24, height: 24 }}
                      />
                      <Typography
                        style={{
                          fontSize: 16,
                          fontWeight: 400,
                          color:
                            document?.fileList.length === 5
                              ? 'rgba(255, 255, 255, 0.15'
                              : 'white',
                        }}>
                        Upload file
                      </Typography>
                    </div>
                  </Upload>
                </Form.Item>
                <Typography
                  style={{
                    fontSize: isTablet ? 10 : 12,
                    fontWeight: 400,
                    color: 'rgba(242, 138, 40, 1)',
                    marginTop: '5px',
                  }}>
                  {'*Maximum 5 files allowed'}
                </Typography>
                <div className='list-file-upload'>
                  <Row gutter={{ md: 10 }}>
                    {Array.isArray(document?.fileList) &&
                      document?.fileList?.map(
                        (item: UploadFile, index: number) => (
                          <Col
                            key={index}
                            xs={24}
                            lg={12}
                            style={{ marginBottom: '10px' }}>
                            <div className='list-file-upload-item-container'>
                              <Typography
                                style={{
                                  fontSize: 16,
                                  fontWeight: 400,
                                  color: 'white',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  direction: 'ltr',
                                  textAlign: 'left',
                                }}>
                                {item.name}
                              </Typography>
                              <TrashIcon
                                className='icon-animation trans-icon'
                                onClick={() => handleRemoveFile(item)}
                              />
                            </div>
                          </Col>
                        )
                      )}
                  </Row>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={24} md={10}>
          <div
            style={{ display: 'flex', justifyContent: 'start', width: '100%' }}>
            <div className='request-money-frame-payment'>
              <Typography className='title-payment'>Payment</Typography>
              <div className='payment-fee'>
                <TextFee
                  title={'Transfer fee'}
                  value={
                    transferFee === null
                      ? `-`
                      : `${formatNumber(
                        Number(transferFee),
                        checked === listPayFee[0].value
                          ? paymentDecimal
                          : beneficiaryDecimal
                      )} ${checked === listPayFee[0].value
                        ? paymentCurrencySymbol
                        : beneficiaryCurrencySymbol
                      }`
                  }
                />
                <TextFee
                  title={'Service fee'}
                  value={
                    serviceFee === null
                      ? `-`
                      : `${formatNumber(
                        Number(
                          serviceFee.toFixed(
                            checked === listPayFee[0].value
                              ? paymentDecimal
                              : beneficiaryDecimal
                          )
                        ),
                        checked === listPayFee[0].value
                          ? paymentDecimal
                          : beneficiaryDecimal
                      )} ${checked === listPayFee[0].value
                        ? paymentCurrencySymbol
                        : beneficiaryCurrencySymbol
                      }`
                  }
                />
                <TextFee
                  title={'Total fee'}
                  value={
                    transferFee === null || serviceFee === null
                      ? `-`
                      : `${formatNumber(
                        Number(
                          (serviceFee + transferFee).toFixed(
                            checked === listPayFee[0].value
                              ? paymentDecimal
                              : beneficiaryDecimal
                          )
                        ),
                        checked === listPayFee[0].value
                          ? paymentDecimal
                          : beneficiaryDecimal
                      )} ${checked === listPayFee[0].value
                        ? paymentCurrencySymbol
                        : beneficiaryCurrencySymbol
                      }`
                  }
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    width: '100%',
                  }}>
                  <div className='total-to-pay'>
                    <Typography
                      style={{
                        fontSize: isTablet ? 16 : 20,
                        fontWeight: 700,
                        color: 'white',
                        minWidth: '150px',
                      }}>
                      Total to pay
                    </Typography>
                    <Typography.Text className='text-gradient'>
                      {handleTotalTopay()}
                    </Typography.Text>
                  </div>
                  <Typography.Text
                    style={{
                      fontSize: isTablet ? 14 : 16,
                      fontWeight: 400,
                      color: 'white',
                      textAlign: 'center',
                    }}>
                    You Will Receive:{' '}
                    <Typography.Text
                      style={{
                        fontSize: isTablet ? 14 : 16,
                        fontWeight: 400,
                        color: 'rgba(242, 138, 40, 1)',
                        textAlign: 'center',
                      }}>
                      {handleBeneficiaryGet()}
                    </Typography.Text>
                  </Typography.Text>
                </div>
              </div>
              <div className='payment-director'>
                <Typography
                  style={{
                    fontSize: isTablet ? 12 : 14,
                    fontWeight: 600,
                    color: 'white',
                  }}>
                  Payment Method
                </Typography>
                <div className={'director-transfer-selected-border'}>
                  <div className='director-transfer-content'>
                    <span className='title'>Provider</span>
                    <Popover
                      content={
                        <CompanyDefault
                          setOpenCompanyDefault={setOpenCompanyDefault}
                          dataCompaniesDefault={dataCompaniesDefault}
                          companyDefaultSelected={companyDefaultSelected}
                          loadingDefaulCompany={loadingDefaulCompany}
                          setSearchDefaultCompany={setSearchDefaultCompany}
                          onChange={(item: any) => {
                            setCompanyDefaultSelected(item)
                            setBankAccountSelectedProvider(null)
                            setWalletSelectedProvider(null)
                            form?.setFieldValue('companies_default', item?.id)
                            form?.resetFields([
                              'providerWallet',
                              'providerBankAccount',
                            ])
                          }}
                        />
                      }
                      trigger='click'
                      arrow={false}
                      overlayInnerStyle={{ borderRadius: '2px', padding: 0 }}
                      placement={'bottomRight'}
                      open={openCompanyDefault}
                      onOpenChange={onOpenChange}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '151px',
                          height: '24px',
                          padding: '3px 12px',
                          border: `1px solid ${disabledProvider
                            ? 'rgba(255, 255, 255, 0.15)'
                            : '#1DABF1'
                            }`,
                          borderRadius: '8px',
                          cursor: disabledProvider ? 'default' : 'pointer',
                        }}>
                        <Typography
                          className='company_default-text'
                          style={{
                            color: disabledProvider
                              ? 'rgba(255, 255, 255, 0.15)'
                              : '#1DABF1',
                          }}>
                          {companyDefaultSelected?.name || 'Select a provider'}
                        </Typography>
                        <CaretDown />
                      </div>
                    </Popover>
                  </div>
                  {form?.getFieldValue('companies_default') ? (
                    <>
                      {(
                        dataAllCurrency.find(
                          (item: any) => item.id === paymentCurrency
                        ) as any
                      )?.type === 'FIAT' ? (
                        <SelectBankAccountProvider
                          isTablet={isTablet}
                          form={form}
                          listBankAccountProvider={listBankAccountProvider}
                          bankAccountSelectedProvider={
                            bankAccountSelectedProvider
                          }
                          setBankAccountSelectedProvider={
                            setBankAccountSelectedProvider
                          }
                          paramsBankAccountProvider={paramsBankAccountProvider}
                          setParamsBankAccountProvider={
                            setParamsBankAccountProvider
                          }
                          companyDefaultSelected={companyDefaultSelected}
                        />
                      ) : (
                        <SelectWalletProvider
                          isTablet={isTablet}
                          form={form}
                          listWalletProvider={listWalletProvider}
                          walletSelectedProvider={walletSelectedProvider}
                          setWalletSelectedProvider={setWalletSelectedProvider}
                          paramsWalletProvider={paramsWalletProvider}
                          setParamsWalletProvider={setParamsWalletProvider}
                          companyDefaultSelected={companyDefaultSelected}
                        />
                      )}
                    </>
                  ) : null}
                </div>
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <CustomButton
                  type='submit'
                  text={'Submit Order'}
                  size={isTablet ? 'medium' : 'large'}
                  width={'100%'}
                  disabled={
                    // !isAllFieldsFilled ||
                    loadingFee ||
                    loadingCreate ||
                    // form.getFieldError('payment_value')?.length > 0 ||
                    // form.getFieldError('beneficiary_value')?.length > 0 ||
                    // payment_value && Number(payment_value) <= 0 ||
                    // beneficiary_value && Number(beneficiary_value) <= 0 ||
                    beneficiaryValueError ||
                    isNegativeBeneficiaryReceive()
                  }
                />
                <Typography
                  style={{
                    fontSize: isTablet ? 10 : 12,
                    fontWeight: 400,
                    color: 'rgba(242, 138, 40, 1)',
                  }}>
                  {
                    '*the rate is applicable for 1 minute, please submit order within 1 minute.'
                  }
                </Typography>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <LoadingOverlay
        loading={loadingCreate || loadingDetail || loadingDefaultValue}
      />
      <ConfirmModal
        type='warning'
        title='Please Confirm Changes'
        description='Fee or beneficiary receive has been updated. Take a moment to review before continuing.'
        open={showReviewFee}
        onCancel={() => null}
        onSubmit={handleContinueReview}
        isLoading={false}
        textSubmit='OK'
        showButtonCancel={false}
      />
    </Form>
  )
}

export default FormTransfer
